import * as React from 'react';
import { cn } from '@/lib/utils';

export interface BottomCurveProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 円の直径（px） */
  diameter?: number;
}

export interface BottomCurveContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** コンテナの高さ */
  height?: string | number;
}

/**
 * BottomCurve - 正円背景デコレーションコンポーネント
 *
 * 正円を背景として配置し、ClubLayoutのmax-width(432px)からはみ出した部分を
 * overflow:hiddenでクリッピングします。ページボトムの装飾として使用します。
 * 円の下部がContainerの底辺に配置されます。
 *
 * @example
 * ```tsx
 * // 基本的な使い方（ClubLayoutで使用）
 * <ClubLayout>
 *   <BottomCurveContainer>
 *     <div>コンテンツ</div>
 *     <BottomCurve className="bg-[#F6F5F4]" diameter={584} />
 *   </BottomCurveContainer>
 * </ClubLayout>
 *
 * // サイズを指定
 * <BottomCurve className="bg-blue-500" diameter={400} />
 *
 * // グラデーション
 * <BottomCurve
 *   className="bg-gradient-to-tr from-[#F6F5F4] to-white"
 *   diameter={584}
 * />
 * ```
 */
export const BottomCurve = React.forwardRef<HTMLDivElement, BottomCurveProps>(
  ({ className, diameter = 584, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'absolute pointer-events-none rounded-full z-0',
          'bottom-0 left-1/2 -translate-x-1/2',
          className,
        )}
        style={{
          width: `${diameter}px`,
          height: `${diameter}px`,
          ...style,
        }}
        {...props}
      />
    );
  },
);

BottomCurve.displayName = 'BottomCurve';

/**
 * BottomCurveContainer - BottomCurveを配置するためのコンテナ
 *
 * ClubLayout内でBottomCurveを使用する際に、relativeポジションとoverflowを提供します
 *
 * @example
 * ```tsx
 * // デフォルト
 * <BottomCurveContainer>
 *   <div>コンテンツ</div>
 *   <BottomCurve className="bg-[#F6F5F4]" diameter={584} />
 * </BottomCurveContainer>
 *
 * // 高さを指定
 * <BottomCurveContainer height={300}>
 *   <div>コンテンツ</div>
 *   <BottomCurve className="bg-[#F6F5F4]" diameter={584} />
 * </BottomCurveContainer>
 *
 * // Tailwindのクラスで指定
 * <BottomCurveContainer className="h-64">
 *   <div>コンテンツ</div>
 *   <BottomCurve className="bg-[#F6F5F4]" diameter={584} />
 * </BottomCurveContainer>
 * ```
 */
export const BottomCurveContainer = React.forwardRef<
  HTMLDivElement,
  BottomCurveContainerProps
>(({ className, children, height, style, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('relative overflow-hidden', className)}
      style={{
        height: typeof height === 'number' ? `${height}px` : height,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
});

BottomCurveContainer.displayName = 'BottomCurveContainer';
